import { Cell, Row, Section, Vector } from "./model";

/**
 * Constructors for blank notation structures.
 *
 * These take their sizes as explicit arguments (`taalCount`, `vectorLength`)
 * rather than reading them off ambient state. The old versions reached into
 * `this.file.fileOptions` / `this.taalCount` on the EditorFile instance, which
 * is exactly the coupling that made the class impossible to test or run
 * server-side.
 *
 * Only two public operations need them (addRow and addSection), plus the
 * initial parse — so threading two extra parameters costs very little.
 */

export function blankSubCell(): string {
    return "";
}

/** A cell always starts with exactly one empty subcell. */
export function blankCell(): Cell {
    return [blankSubCell()];
}

/** One matra, with one cell per file-wide track. */
export function blankVector(vectorLength: number): Vector {
    const cells: Cell[] = [];
    for (let i = 0; i < vectorLength; i++) {
        cells.push(blankCell());
    }
    return { cells };
}

/** One avartan: `taalCount` matras wide. */
export function blankRow(taalCount: number, vectorLength: number): Row {
    const vectors: Vector[] = [];
    for (let i = 0; i < taalCount; i++) {
        vectors.push(blankVector(vectorLength));
    }
    return {
        vectors,
        options: { additionalRowVectorElements: 0 },
        subRowNames: [],
        renderMethod: [],
    };
}

/**
 * A section containing a single blank row.
 *
 * `sectionCount` only feeds the default name. It is passed in (rather than
 * derived from `sections.length`) to preserve the existing naming behaviour
 * exactly — the counter it came from is not the same as the section count.
 * See the `deleteSection` bug noted in the characterization tests.
 */
export function blankSection(
    taalCount: number,
    vectorLength: number,
    sectionCount: number
): Section {
    return {
        rows: [blankRow(taalCount, vectorLength)],
        name: `New Section ${sectionCount + 1}`,
    };
}
